import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetCourseHearingsQuery,
  useGetCourseHearingQuery,
  useReviewHearingSubmissionMutation,
  teachurKeys,
  type Course,
  type UserCourse,
} from '@teachur/http-client';
import { type ReviewFormSubmitData } from '@new/common/ui/ReviewForm/ReviewForm';

interface UseCourseReviewProps {
  userCourse: UserCourse;
  course: Course;
  studentId: number;
  // The current attempt's scheduled call / video call id. Attempts are keyed by
  // this on the course hearing, so it's how we locate the submission to grade.
  scheduledCallId: string;
}

export const useCourseReview = ({ course, studentId, scheduledCallId }: UseCourseReviewProps) => {
  const queryClient = useQueryClient();

  // The list is a slim projection (id/derivedStatus/latestAttempt only) — it
  // carries no attempts/currentAttempt/hearingSubmission. Use it only to resolve
  // the hearing id (a student has at most one hearing per course, and the list
  // is already filtered by courseId + studentId), then fetch the full hearing.
  const { data: hearingsResponse } = useGetCourseHearingsQuery(
    { courseId: course.id, studentId },
    { enabled: !!course.id && !!studentId }
  );

  const hearingId = hearingsResponse?.data?.[0]?.id;

  // Detail read carries attempts + currentAttempt + hearingSubmission — the
  // grade target. Reading them off the slim list left hearingSubmissionId
  // undefined, so review submits failed with "No hearing submission found".
  const { data: hearing } = useGetCourseHearingQuery(hearingId, {
    enabled: !!hearingId,
  });

  // Prefer the attempt whose scheduled call matches this session; fall back to
  // the hearing's currentAttempt (the single open one) when the ids line up.
  const attempt =
    hearing?.attempts?.find((a) => a.id === scheduledCallId || a.videoCallId === scheduledCallId) ??
    hearing?.currentAttempt ??
    null;
  const hearingSubmissionId = attempt?.hearingSubmission?.id;

  const reviewSubmission = useReviewHearingSubmissionMutation();

  const handleSubmit = useCallback(
    (data: ReviewFormSubmitData) => {
      if (!hearingSubmissionId) {
        toast.error('No hearing submission found to review.');
        return;
      }

      reviewSubmission.mutate(
        {
          submissionId: hearingSubmissionId,
          data: {
            status: data.approved ? 'approved' : 'notYet',
            note: data.generalNote,
            grades: data.grades.map((g) => ({
              goalId: g.goalId,
              grade: g.rating,
              note: g.note,
            })),
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teachurKeys.courseHearings.all });
            queryClient.invalidateQueries({
              queryKey: teachurKeys.scheduledCalls.list({
                courseId: course.id,
                studentId,
              }),
            });
            toast.success('Student course review updated!');
          },
          onError: () => toast.error('An error occurred!'),
        }
      );
    },
    [hearingSubmissionId, course.id, studentId, queryClient, reviewSubmission]
  );

  return {
    handleSubmit,
  };
};
